// Shared colour/label vocabulary for the composite workbench.
//
// Colours reference V5 design tokens (thread-v5.css) so light/dark
// themes stay consistent; SVG elements consume them via style props.

import type { CompositeClassification, SourceClass } from "../../../api/composite";

export const SOURCE_CLASS_LABEL: Record<SourceClass, string> = {
  rom: "ROM",
  cfd: "CFD",
  wind_tunnel: "WT",
  flight_test: "FT",
  manual: "Manual",
};

export const SOURCE_CLASS_COLOR: Record<SourceClass, string> = {
  rom: "var(--v5-ink-4)",
  cfd: "var(--v5-blue)",
  wind_tunnel: "var(--v5-amber)",
  flight_test: "var(--v5-primary)",
  manual: "var(--v5-warn)",
};

export const COMPOSITE_COLOR = "var(--v5-ink)";

export const CLASSIFICATION_LABEL: Record<CompositeClassification, string> = {
  ok: "ok",
  low_confidence: "low confidence",
  conflicting: "conflicting",
  extrapolation: "extrapolation",
  no_data: "no data",
};

export const QUANTITY_OPTIONS: Array<{ value: string; label: string }> = [
  // Vehicle aero coefficients
  { value: "cl", label: "CL — lift" },
  { value: "cd", label: "CD — drag" },
  { value: "cm", label: "Cm — pitching moment" },
  { value: "l_d", label: "L/D — lift-to-drag" },
  { value: "cl_roll", label: "Cl — rolling moment" },
  // Inlet quantities
  { value: "pressure_recovery", label: "Pressure recovery" },
  { value: "distortion_dc60", label: "Distortion DC60" },
  { value: "mass_flow_ratio", label: "Mass-flow ratio" },
  { value: "spillage_drag_cd", label: "Spillage drag ΔCD" },
  { value: "operability_margin", label: "Operability margin" },
  { value: "wall_temp_k", label: "Wall temperature (K)" },
];

export function formatValue(value: number | null | undefined, sigma?: number | null): string {
  if (value == null) return "—";
  const digits = Math.abs(value) >= 100 ? 1 : Math.abs(value) >= 1 ? 3 : 4;
  const base = value.toFixed(digits);
  if (sigma == null) return base;
  return `${base} ±${sigma.toFixed(Math.min(digits, 4))}`;
}

export function formatCondition(condition: Record<string, number | string>): string {
  const mach = Number(condition.mach ?? 0);
  const altKm = Number(condition.altitude_m ?? 0) / 1000;
  const alpha = Number(condition.alpha_deg ?? 0);
  return `M ${mach.toFixed(2)} · ${altKm.toFixed(1)} km · α ${alpha.toFixed(1)}°`;
}
